import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { API_REQUEST, apiError, apiSuccess, hideLoader, showLoader } from '../actions'
import config from '../config'
import { AuthHeaderOverrides, authHeaderSelector } from '../selectors'
import { Middleware } from '../store'
import { shouldSendDatadog } from '../util/sentry'
import { datadogLogs } from '@datadog/browser-logs'

const API = config.api
const cancel: any = {}

export type RequestConfig = AxiosRequestConfig & {
  payload?: any
  type: string
}

export const doRequest = async <T>(config: RequestConfig): Promise<AxiosResponse<T> | undefined> => {
  const { headers, method, payload, params, responseType, type, url, validateStatus } = config

  if (!url) {
    return Promise.reject('invalid url')
  }

  if (cancel[type]) {
    cancel[type](`${type} cancelled (url: ${url})`)
  }

  const reqConfig: AxiosRequestConfig = {
    method,
    data: payload || 'boolean' === typeof payload || payload === '' ? JSON.stringify(payload) : null,
    headers,
    params,
    responseType,
    cancelToken: new axios.CancelToken(function (c: any) {
      cancel[type] = c
    }),
  }

  if (url.indexOf('http') === 0) {
    reqConfig.url = url
  } else {
    reqConfig.url = API + url
  }

  if (validateStatus) {
    reqConfig.validateStatus = validateStatus
  }

  return axios(reqConfig)
    .then((response) => {
      cancel[type] = null
      return response
    })
    .catch((error: AxiosError) => {
      cancel[type] = null
      // Returning an undefined response for cancellations to break the
      // Promise chain - calling functions must be ready to handle an undefined
      // response.
      // For this app, a cancellation is almost always fine, so there is
      // no need to continue the chain or report on errors.
      // This may be confusing and likely worth a refactor given that
      // Axios updated their cancellation handlings away from CancelTokens.
      if (axios.isCancel(error)) {
        return undefined
      }

      if (shouldSendDatadog(error, config.type)) {
        // TODO: determin if we need the additional context provided by setSentryErrorData
        datadogLogs.logger.error('API request error', {
          error: error,
          reqType: config.type,
          url: reqConfig.url,
          method: method,
        })
        // Sentry.withScope(function (scope) {
        //   setSentryErrorData(scope, error)
        //   scope.setTag('reqType', config.type)
        //   scope.setContext('request', {
        //     url: reqConfig.url,
        //     method: method,
        //   })
        //   Sentry.captureException(error)
        // })
      }

      console.error('API ERROR: ', error)
      throw error
    })
}

export const apiMiddleware: Middleware = ({ dispatch, getState }) => (next) => (action) => {
  next(action)

  if (action.type.includes(API_REQUEST)) {
    const { feature, resource } = action.meta

    dispatch(showLoader(feature))

    let overrides: AuthHeaderOverrides | undefined
    if (action?.meta?.asOwnerOverride || action?.meta?.scope) {
      overrides = {
        scope: action?.meta?.scope,
        asOwnerOverride: action?.meta?.asOwnerOverride,
      }
    }
    doRequest({
      ...action.meta,
      // TO-13776: TS throws an error re: the optional param here
      // unless you're running a higher TypeScript version than this project.
      // it works in reality, though.
      // @ts-ignore
      headers: authHeaderSelector(getState(), overrides),
      payload: action.payload,
      type: action.type,
    })
      .then((response) => {
        if (!response) {
          return
        }
        if (401 === response.status) {
          dispatch(apiError(response.data, feature))
        } else if (response.status > 299) {
          dispatch(apiError(response.data, feature))
          dispatch(hideLoader(feature))
        } else {
          dispatch(apiSuccess(response.data, feature, resource, response.headers))
          dispatch(hideLoader(feature))
        }
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.data) {
          dispatch(apiError(error.response.data, feature))
          dispatch(hideLoader(feature))
        } else {
          dispatch(apiError(error.response, feature))
          dispatch(hideLoader(feature))
        }
      })
  }
}
